import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import './AgentVerification.css';
import stateCityData from '../data/state_city.json';
import { isValidLicenseNumberForState, TX_FIRM_LICENSE_TYPES, canonicalLicenseNumber } from '../utils/license';

// Map technical API errors to user-friendly messages (mirrors mobile BrokerVerificationScreen)
const mapBrokerVerificationError = (error) => {
  const detail = (
    error?.response?.data?.verificationDetails ||
    error?.response?.data?.message ||
    error?.message || ''
  ).toLowerCase();
  if (detail.includes('already registered') || detail.includes('already exists'))
    return 'This broker license is already registered with another account.';
  if (detail.includes('not found') || detail.includes('no record') || detail.includes('no match'))
    return 'Broker license was not found in the registry.';
  if (detail.includes('expir'))
    return 'This broker license appears to be expired.';
  if (detail.includes('inactive') || detail.includes('suspended') || detail.includes('revoked'))
    return 'This broker license is not currently active.';
  if (detail.includes('not available') || detail.includes('not supported'))
    return 'Broker license verification is not available for this state yet.';
  return 'Unable to verify your broker license. Please check your details and try again.';
};

// Combines street/city/state/zip into a single display string, e.g. "123 Main St, City, CA 90001"
// (mirrors mobile BrokerRegistrationScreen's formatFullAddress helper)
const formatFullAddress = (street, city, state, zip) => {
  const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, cityStateZip].filter(Boolean).join(', ');
};

const BrokerVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Invite / route data ───────────────────────────────────────────────────
  const inviteData = (() => {
    if (location.state?.inviteData) return location.state.inviteData;
    const s = sessionStorage.getItem('inviteData');
    return s ? JSON.parse(s) : {};
  })();

  // ── Form fields ───────────────────────────────────────────────────────────
  const [selectedState,      setSelectedState]      = useState('');
  const [licenseNumber,      setLicenseNumber]      = useState('');
  const [companyName,        setCompanyName]        = useState('');
  // Support both API spellings; inviteData may carry either.
  // The Brokerage License ID field was removed from the form — this now only
  // carries the value locked in from an invite (shown in the invite banner above).
  const invitedBrokerageLicenseId = inviteData.brokerageLicenseId || inviteData.brokerageLicenceId || '';
  const brokerageLicenceId = invitedBrokerageLicenseId;

  // ── Validation state machine: 'idle'|'validating'|'valid'|'invalid' ───────
  const [validationState,   setValidationState]   = useState('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [verificationResponse, setVerificationResponse] = useState(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isSaving,     setIsSaving]     = useState(false);
  const [submitError,  setSubmitError]  = useState('');
  const [displayName,  setDisplayName]  = useState('there');

  const debounceRef = useRef(null);

  // ── Pre-fill from invite data + logged-in user ────────────────────────────
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    setDisplayName(inviteData.firstName || storedUser.firstName || 'there');
    setSelectedState(inviteData.state || '');
    setLicenseNumber(inviteData.licenseNumber || inviteData.brokerLicenseNumber || '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // ── Core: call check-broker-license API ──────────────────────────────────
  const verifyLicense = useCallback(async (license, state) => {
    if (!license || !state) return;

    setValidationState('validating');
    setValidationMessage('');
    setVerificationResponse(null);

    try {
      const res  = await apiClient.post(API_ENDPOINTS.verifyLicense, {
        licenseNumber: license.trim(),
        state,
        role: 'BROKER',
      });

      const data = res.data?.data || res.data;

      setVerificationResponse(data);

      if (data?.status === 'valid') {
        setValidationState('valid');
        setValidationMessage('Broker license verified successfully!');
        // Pre-fill company name from affiliated firm in the registry
        const affiliateFirm = Array.isArray(data.affiliations)
          ? data.affiliations.find(
              a => a.licenseType === 'Corporation' || a.licenseType === 'Brokerage' || a.licenseType === 'Broker' ||
                 TX_FIRM_LICENSE_TYPES.includes(a.licenseType)
            )
          : null;
        const firmName = affiliateFirm?.fullName || affiliateFirm?.name || data?.brokerageName || '';
        if (firmName) setCompanyName(prev => prev || firmName);
      } else {
        setValidationState('invalid');
        setValidationMessage(
          data?.message ||
          'Broker license could not be verified. Please check your details.'
        );
      }
    } catch (error) {
      console.error('[BrokerVerification] verify-license error:', error);
      setValidationState('invalid');
      setValidationMessage(
        error?.response?.status === 404
          ? 'Broker license was not found in the registry.'
          : mapBrokerVerificationError(error)
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Field change handlers ─────────────────────────────────────────────────
  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setLicenseNumber('');
    setValidationState('idle');
    setValidationMessage('');
    setVerificationResponse(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleLicenseChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
    setLicenseNumber(value);
    setValidationState('idle');
    setValidationMessage('');
    setVerificationResponse(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Auto-verify after 1 second once state is selected and the number passes
    // the shared minimum-length check.
    if (selectedState && isValidLicenseNumberForState(value, selectedState)) {
      debounceRef.current = setTimeout(() => verifyLicense(value, selectedState), 1000);
    }
  };

  const handleVerifyClick = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    verifyLicense(licenseNumber, selectedState);
  };

  // ── Derived UI flags ──────────────────────────────────────────────────────
  const canVerify        = selectedState && licenseNumber.trim().length >= 3;
  const showVerifyBtn    = canVerify && validationState !== 'validating' && validationState !== 'valid';
  const isButtonDisabled = validationState !== 'valid' || isSaving;

  // ── Submit (Continue) ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isButtonDisabled) return;

    setIsSaving(true);
    setSubmitError('');

    const firebaseToken = localStorage.getItem('firebaseToken');
    const storedUser    = JSON.parse(localStorage.getItem('user') || '{}');
    const isLoggedIn    = !!firebaseToken;

    if (inviteData.alreadyRegistered || isLoggedIn) {
      // ── Existing / Switch Role user: update broker profile via API ─────────
      try {
        const userId = storedUser.id || storedUser.firebaseUid || '';

        if (userId) {
          const affiliateFirm = Array.isArray(verificationResponse?.affiliations)
            ? verificationResponse.affiliations.find(
                a => a.licenseType === 'Corporation' || a.licenseType === 'Brokerage' || a.licenseType === 'Broker' ||
                 TX_FIRM_LICENSE_TYPES.includes(a.licenseType)
              )
            : null;
          // PUT /api/v1/users/{userId}/broker-profile (matches mobile updateBrokerProfile)
          await apiClient.put(
            API_ENDPOINTS.brokerProfile(userId),
            {
              companyName:          affiliateFirm?.name || companyName.trim(),
              companyLicenseNumber: affiliateFirm?.licenseNumber     || undefined,
              companyLicenseState:  selectedState                     || undefined,
              companyAddress:       verificationResponse?.addressLine1 || verificationResponse?.brokerageAddress || undefined,
              companyPhone:         verificationResponse?.brokeragePhone   || undefined,
              brokerageLicenseId:   brokerageLicenceId.trim()         || undefined,
            },
            { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
          );

          // Add the invited broker role to the user's account (preserves BROKER_MANAGER / BROKER_ADMIN)
          try {
            await apiClient.post(
              API_ENDPOINTS.userRoles(userId),
              { role: (inviteData.role?.toUpperCase() || 'BROKER') },
              { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
            );
          } catch (roleErr) {
            console.error('[BrokerVerification] addRole error:', roleErr);
          }

          // Refresh profile in localStorage so Switch Role picks it up
          try {
            const profileRes = await apiClient.get(API_ENDPOINTS.userMe, {
              headers: { 'Authorization': `Bearer ${firebaseToken}` },
            });
            const updated = profileRes.data?.data || profileRes.data?.content || profileRes.data;
            if (updated && typeof updated === 'object') {
              localStorage.setItem('user', JSON.stringify({ ...storedUser, ...updated }));
            }
          } catch { /* non-critical */ }
        }

        // Accept any pending invite
        if (inviteData.key) {
          try {
            if (inviteData.type === 'user') {
              await apiClient.post(
                API_ENDPOINTS.acceptUserInvite(inviteData.key),
                {},
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            } else if (inviteData.type === 'transaction') {
              const userName = [storedUser.firstName, storedUser.lastName].filter(Boolean).join(' ');
              await apiClient.post(
                API_ENDPOINTS.acceptInvite,
                { invitationKey: inviteData.key, userId: storedUser.id || '', userName },
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            }
          } catch { /* invite accept failure is non-critical */ }
        }

        sessionStorage.removeItem('inviteData');
        sessionStorage.removeItem('brokerVerificationData');

        const txnId = inviteData.transactionId;
        navigate(txnId ? `/transaction/${txnId}` : '/dashboard', { replace: true });
      } catch (err) {
        console.error('[BrokerVerification] profile update error:', err);
        setSubmitError(
          err?.response?.data?.message ||
          'Failed to save your broker profile. Please try again.'
        );
        setIsSaving(false);
      }
    } else {
      // ── New user: navigate to Register with verification data ─────────────
      // Extract the broker's affiliated firm from verify-license affiliations
      const affiliateFirm = Array.isArray(verificationResponse?.affiliations)
        ? verificationResponse.affiliations.find(
            a => a.licenseType === 'Corporation' || a.licenseType === 'Brokerage' || a.licenseType === 'Broker' ||
                 TX_FIRM_LICENSE_TYPES.includes(a.licenseType)
          )
        : null;

      // Registry responses may split a single fullName into
      // firstName/middleName/lastName server-side (see
      // AgentLicenseFullLookupService.splitFullNameIfMissing) — middleName is
      // only trustworthy from that split. ARELLO fallback's middleName field
      // is sparse and unverified, so it's deliberately not used for autofill.
      const isRegistrySource = !!verificationResponse?.source && verificationResponse.source !== 'ARELLO';

      // Licensed City: only trustworthy when the registry's mailing-address
      // state matches the licensing jurisdiction (selectedState) — otherwise
      // `city` is just an out-of-state mailing city, not where they're
      // actually licensed to practice. Left blank for the licensee to enter
      // themselves whenever we can't confirm the match. See
      // import_licenses.py's mailingState field.
      const licenseCityFromVerification =
        (isRegistrySource && verificationResponse?.mailingState === selectedState)
          ? (verificationResponse?.city || '')
          : '';
      // Same trust rule as Licensed City — a Colorado zip code from the
      // registry is only meaningful when the mailing state actually matches
      // the license jurisdiction.
      const licenseZipFromVerification =
        (isRegistrySource && verificationResponse?.mailingState === selectedState)
          ? (verificationResponse?.zipCode || '')
          : '';

      const brokerData = {
        role:                 inviteData.role?.toLowerCase() || 'broker',
        state:                selectedState,
        stateCode:            selectedState,
        licenseCityFromVerification,
        licenseZipFromVerification,
        brokerLicenseNumber:  canonicalLicenseNumber(licenseNumber, verificationResponse, selectedState),
        companyName:          affiliateFirm?.fullName || affiliateFirm?.name || companyName.trim(),
        brokerageName:        affiliateFirm?.fullName || affiliateFirm?.name || verificationResponse?.brokerageName || companyName.trim() || '',
        companyLicenseNumber: affiliateFirm?.licenseNumber || '',
        companyLicenseState:  selectedState,
        licenseType:          affiliateFirm?.licenseType || '',
        verificationId:       verificationResponse?.id || '',
        firstName:            verificationResponse?.firstName || inviteData.firstName || storedUser.firstName || '',
        middleName:           isRegistrySource ? (verificationResponse?.middleName || '') : '',
        lastName:             verificationResponse?.lastName  || inviteData.lastName  || storedUser.lastName  || '',
        email:                verificationResponse?.email     || inviteData.email     || storedUser.email     || '',
        phone:                verificationResponse?.phone     || inviteData.phone     || '',
        // Broker's own personal (mailing) address from the license registry,
        // falling back to the firm's address — mirrors mobile
        // brokerStreet/brokerCity/brokerZip priority. Reported exactly as the
        // registry/ARELLO gives it (any state, not just the license
        // jurisdiction).
        street:               verificationResponse?.addressLine1 || affiliateFirm?.addressLine1 || '',
        city:                 verificationResponse?.city         || affiliateFirm?.city         || '',
        // Real mailing state — NOT verificationResponse.state, which is
        // always the hardcoded license jurisdiction and would silently
        // corrupt this address for an out-of-state mailing record. ARELLO
        // responses have no separate mailingState, so state is the correct
        // (and only) signal there.
        personalState:        verificationResponse?.mailingState || verificationResponse?.state || affiliateFirm?.state || '',
        zipCode:              verificationResponse?.zipCode      || affiliateFirm?.zipCode      || '',
        // Brokerage/firm address — from the affiliated firm found in the license registry
        // (mirrors mobile's formatFullAddress(arelloBrokerageAddress, city, state, zip))
        companyAddress:       formatFullAddress(affiliateFirm?.addressLine1, affiliateFirm?.city, affiliateFirm?.state, affiliateFirm?.zipCode),
        brokerageAddress:     formatFullAddress(affiliateFirm?.addressLine1, affiliateFirm?.city, affiliateFirm?.state, affiliateFirm?.zipCode),
        brokerageCity:        affiliateFirm?.city    || '',
        brokerageState:       affiliateFirm?.state   || '',
        brokerageZip:         affiliateFirm?.zipCode || '',
        companyPhone:         affiliateFirm?.phone   || verificationResponse?.brokeragePhone || '',
        licenseExpirationDate: verificationResponse?.expirationDate || verificationResponse?.licenseExpirationDate || '',
        affiliatedFirmLicenseNumber: affiliateFirm?.licenseNumber || '',
        brokerageLicenseId: brokerageLicenceId.trim() || '',
        key:          inviteData.key  || '',
        type:         inviteData.type || '',
        transactionId: inviteData.transactionId || '',
      };
      sessionStorage.setItem('brokerVerificationData', JSON.stringify(brokerData));
      navigate('/register', {
        state: { brokerData, inviteData: { ...inviteData, ...brokerData } },
        replace: false,
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="agent-verification-page">
      <div className="verification-container">
        <div className="verification-card">

          {/* Loading overlay — shown while saving broker profile */}
          {isSaving && (
            <div className="av-loading-overlay">
              <div className="av-loading-spinner" />
              <p className="av-loading-text">Adding Broker role...</p>
            </div>
          )}

          {/* Header */}
          <div className="verification-header">
            <p className="greeting-text">Hi {displayName},</p>
            <h1 className="verification-title">You're almost in!</h1>
            <p className="verification-subtitle">
              Let's verify your broker license to get started.
            </p>
          </div>

          {/* Joining-brokerage card — shown for designated-broker activation invites */}
          {(inviteData.companyName || inviteData.brokerageLicenseId || inviteData.brokerageLicenceId) && (
            <div className="av-brokerage-invite-card">
              <p className="av-brokerage-invite-label">
                {inviteData.isDesignatedBrokerInvite
                  ? "You're being invited as the Designated Broker of"
                  : "You're joining"}
              </p>
              {inviteData.companyName && (
                <p className="av-brokerage-invite-name">{inviteData.companyName}</p>
              )}
              {(inviteData.brokerageLicenseId || inviteData.brokerageLicenceId) && (
                <p className="av-brokerage-invite-licence">
                  Brokerage licence: {inviteData.brokerageLicenseId || inviteData.brokerageLicenceId}
                </p>
              )}
              <p className="av-brokerage-invite-note">
                These details are locked from the invite and cannot be changed here.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="verification-form">

            {/* ── State selector ── */}
            <div className="form-group">
              <select
                value={selectedState}
                onChange={handleStateChange}
                className={`form-select ${!selectedState ? 'placeholder' : ''}`}
                required
              >
                <option value="">Select State</option>
                {stateCityData.map((item) => (
                  <option key={item.stateCode} value={item.stateCode}>
                    {item.state} ({item.stateCode})
                  </option>
                ))}
              </select>
            </div>

            {/* ── Broker License Number ── */}
            <div className="form-group">
              <div className="license-input-wrapper">
                <input
                  type="text"
                  placeholder="Broker License Number"
                  value={licenseNumber}
                  onChange={handleLicenseChange}
                  maxLength={30}
                  autoCapitalize="characters"
                  className={`form-input${validationState === 'invalid' ? ' input-error' : ''}${validationState === 'valid' ? ' input-success' : ''}`}
                />
                {validationState === 'validating' && (
                  <span className="license-verifying">Verifying...</span>
                )}
              </div>
            </div>

            {/* ── Manual "Verify License" button ── */}
            {showVerifyBtn && (
              <button
                type="button"
                className="av-verify-btn"
                onClick={handleVerifyClick}
              >
                Verify License
              </button>
            )}

            {/* ── Validation result row ── */}
            {validationState !== 'idle' && validationMessage && (
              <div className={`av-validation-row av-validation-${validationState}`}>
                <span className="av-validation-icon">
                  {validationState === 'valid'       ? '✓'
                  : validationState === 'validating' ? '…'
                  :                                   '⚠'}
                </span>
                <span className="av-validation-text">{validationMessage}</span>
              </div>
            )}

            {/* ── Submit error ── */}
            {submitError && (
              <div className="status-message error">{submitError}</div>
            )}

            {/* ── Continue button ── */}
            <button
              type="submit"
              className="btn-continue"
              disabled={isButtonDisabled}
            >
              {isSaving ? 'Processing...' : 'Continue'}
            </button>

          </form>

          <div className="verification-footer">
            <p className="terms-text">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="terms-link">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="terms-link">Privacy policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerVerificationPage;
